import { memberCardQuery } from "#features/conversations/hooks/useMemberCard.ts";
import { MEMBER_ID } from "#features/conversations/utils/composerDocument.ts";
import { type MemberDisplay, useMemberDisplayStore } from "#features/conversations/stores/useMemberDisplayStore.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

// As many ids as the BFF looks up in one request.
const MAX_MEMBER_IDS = 50;

// Each named member's photo and job title, by id: what the "@" list found this session, and the rest in one request that
// also fills each member's card. A member the BFF doesn't return is remembered without either, so it isn't asked for again.
export const useMemberDisplays = (memberIds: readonly string[]): ReadonlyMap<string, MemberDisplay> => {
    const memberDisplays = useMemberDisplayStore(state => state.memberDisplays);
    const rememberMembers = useMemberDisplayStore(state => state.rememberMembers);
    const { getMembersByIds } = useAiApiClient();
    const queryClient = useQueryClient();

    // A member the "@" list found has a photo but no card yet; asking for them too has every mention's card ready to hover.
    const missingIds = useMemo(
        () => [...new Set(memberIds)]
            .filter(id => MEMBER_ID.test(id) && (!memberDisplays.has(id) || queryClient.getQueryData(AiQueryKeys.GetMemberCard(id)) === undefined))
            .sort()
            .slice(0, MAX_MEMBER_IDS),
        [memberIds, memberDisplays, queryClient]
    );

    const { data } = useQuery({
        queryKey: AiQueryKeys.GetMembersByIds(missingIds),
        queryFn: async () => {
            const request = getMembersByIds(missingIds);
            // Each member's card reads this same request, so hovering a mention while it's in flight doesn't ask again.
            missingIds.forEach(id => void queryClient.prefetchQuery(memberCardQuery(id, () => request)));
            const members = new Map((await request).map(member => [member.id, member]));

            return missingIds.map(id => ({ id, imageUrl: members.get(id)?.imageUrl ?? undefined, jobTitle: members.get(id)?.jobTitle ?? undefined }));
        },
        enabled: missingIds.length > 0,
        staleTime: Infinity
    });

    useEffect(() => {
        if (data) {
            rememberMembers(data);
        }
    }, [data, rememberMembers]);

    return useScopedDisplays(memberDisplays, memberIds);
};

// Only the named members, the same Map while their entries are unchanged: a member another "@" search found elsewhere
// doesn't hand every message a new Map to render again with.
const useScopedDisplays = (memberDisplays: ReadonlyMap<string, MemberDisplay>, memberIds: readonly string[]) => {
    const scopedRef = useRef<ReadonlyMap<string, MemberDisplay>>(new Map());

    return useMemo(() => {
        const scoped = new Map<string, MemberDisplay>();
        memberIds.forEach(id => {
            const display = memberDisplays.get(id);
            if (display) {
                scoped.set(id, display);
            }
        });
        const previous = scopedRef.current;
        const isUnchanged = scoped.size === previous.size && [...scoped].every(([id, display]) => previous.get(id) === display);
        if (!isUnchanged) {
            scopedRef.current = scoped;
        }

        return scopedRef.current;
    }, [memberDisplays, memberIds]);
};
